import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Megaphone, Plus, ChevronRight, RefreshCcw, MoreVertical, Archive, ArchiveRestore, Trash2, Search, X } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { campaignsAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';
import { formatDateTime } from '../lib/format.js';
import { AutoPauseChip } from '../components/AutoPauseBanner.jsx';
import { stripPauseMarker } from '../lib/campaignPause.js';
import useRevalidate from '../hooks/useRevalidate.js';

const STATUS_BADGE = {
  draft: 'bg-stone-800 text-stone-300 border border-stone-700',
  active: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  completed: 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
};

// List priority: the ones that need attention first. Active campaigns rise to the
// top, then paused, then completed; every other status (draft / not-yet-started)
// falls below and sorts alphabetically. Archived rows always sink to the bottom.
const STATUS_RANK = { active: 0, paused: 1, completed: 2 };
function byStatusPriority(a, b) {
  const aArch = a.archived ? 1 : 0;
  const bArch = b.archived ? 1 : 0;
  if (aArch !== bArch) return aArch - bArch;
  const ra = STATUS_RANK[a.status] ?? 3;
  const rb = STATUS_RANK[b.status] ?? 3;
  if (ra !== rb) return ra - rb;
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
}

export default function Campaigns() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  // Show archived (soft-hidden) campaigns alongside the active ones.
  const [showArchived, setShowArchived] = useState(false);
  // Find a campaign by name or description. Client-side: the whole list is already
  // loaded and it is dozens of rows, not thousands — a round trip would be slower
  // than typing. Matches the description too, because that is where the vertical
  // actually lives ("SMB dental practices — missed new-patient calls").
  const [query, setQuery] = useState('');

  // ── FIND A BUSINESS ACROSS EVERY CAMPAIGN ────────────────────────────────────
  // "Which campaign is this business in?" — the question you actually have when a
  // business calls back and you need their history before picking up.
  //
  // ⚠️ THERE IS NO BACKEND SEARCH FOR THIS. `GET /campaigns/:id/contacts` takes
  // limit/offset/status/sort and no query, and nothing searches across campaigns.
  // So this builds a SLIM INDEX client-side: one pass over each campaign's contacts,
  // keeping only id/company/name/phone/status per row (~100 bytes each, so ~2,400
  // contacts ≈ 240KB) and discarding the rest immediately. Built once, on the first
  // search — never on page load, because most visits never search.
  //
  // Asked Charlie for `GET /campaign-contacts/search?q=`; when that lands this whole
  // block collapses to one request. See docs/channel-charlie-delta.md.
  // Arriving with ?business=<name> (from the Demonstrations page) pre-fills the
  // cross-campaign business search, so a demo row leads to that business's calls
  // without copying the name between screens.
  const [searchParamsInit] = useSearchParams();
  const [bizQuery, setBizQuery] = useState(() => searchParamsInit.get('business') || '');
  // Only true for the URL-driven arrival: it lets a SINGLE unambiguous match go
  // straight into the campaign. Typing must never teleport you mid-keystroke.
  const [autoJump, setAutoJump] = useState(() => !!searchParamsInit.get('business'));
  const [bizIndex, setBizIndex] = useState(null);   // null = never built
  const [indexing, setIndexing] = useState(false);
  const [indexError, setIndexError] = useState('');

  const buildIndex = async (campaignList) => {
    setIndexing(true);
    setIndexError('');
    try {
      const rows = [];
      // Small concurrency: a dozen parallel 5000-row fetches is a lot to ask of one
      // tab at once, and this runs while someone is typing.
      const queue = [...campaignList];
      const worker = async () => {
        while (queue.length) {
          const c = queue.shift();
          try {
            const r = await campaignsAPI.contacts(c.id, { limit: 5000 });
            for (const ct of (r?.data || [])) {
              rows.push({
                campaignId: c.id,
                campaignName: c.name,
                company: ct.company || '',
                name: ct.name || '',
                phone: ct.phoneE164 || '',
                status: ct.status || '',
                outcome: ct.outcome || ''
              });
            }
          } catch { /* one campaign failing must not kill the whole index */ }
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]);
      setBizIndex(rows);
      return rows;
    } catch (err) {
      setIndexError(err.message || 'Could not search contacts.');
      return [];
    } finally {
      setIndexing(false);
    }
  };

  // Build lazily on the first real query, then reuse for every keystroke after.
  //
  // ⛔ `items.length === 0` IS LOAD-BEARING, and so is having items.length in the deps.
  // Arriving with ?business=… (a demo name click) sets bizQuery in the initial state, so
  // this effect fires on MOUNT — before the campaign list has come back. Without the
  // guard it built an index from zero campaigns, and `bizIndex !== null` then locked
  // that empty index in forever, because bizQuery never changes again when it came from
  // the URL rather than the keyboard. The result was a permanent
  // "No business matching X (0 contacts searched)" for a business that exists.
  // Typing always worked, which is exactly why this survived review: the failure needs
  // a query that is present before the data is.
  useEffect(() => {
    const q = bizQuery.trim();
    if (q.length < 2 || bizIndex !== null || indexing || items.length === 0) return;
    const t = setTimeout(() => buildIndex(items), 250);
    return () => clearTimeout(t);
  }, [bizQuery, items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // Which row's action menu (archive / delete) is open, by campaign id.
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const menuRef = useRef(null);

  // `quiet` = a background revalidation: don't flash the spinner and don't raise
  // an error banner over a list that is already on screen and still valid.
  const load = async ({ quiet = false } = {}) => {
    if (!quiet) { setLoading(true); setError(''); }
    try {
      const r = await campaignsAPI.list({ includeArchived: showArchived });
      setItems(r?.data || []);
    } catch (err) {
      if (!quiet && err.status !== 404) setError(err.message || 'Failed to load campaigns.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  // The health monitor auto-pauses campaigns with no user action, so a campaign
  // can stop dialling while this list still shows it active. Nothing on the page
  // would ever trigger a reload, so poll for it.
  useRevalidate(() => load({ quiet: true }), { intervalMs: 30_000 });
  // Reload when the archived toggle flips so the list reflects the new filter.
  useEffect(() => { load(); }, [showArchived]);

  // Close the open action menu on any outside click.
  useEffect(() => {
    if (!menuOpenId) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpenId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpenId]);

  // Hide / unhide from the default list without destroying the campaign.
  const toggleArchived = async (c) => {
    setMenuOpenId(null);
    setBusyId(c.id);
    try {
      await campaignsAPI.setArchived(c.id, !c.archived);
      // If we just archived a row while NOT showing archived, drop it locally;
      // otherwise reload so the badge/state updates in place.
      if (!c.archived && !showArchived) {
        setItems((prev) => prev.filter((x) => x.id !== c.id));
      } else {
        load();
      }
    } catch (err) {
      setError(err.message || 'Failed to update campaign.');
    } finally {
      setBusyId(null);
    }
  };

  // Permanently delete the campaign + its contacts (behind a confirm).
  const remove = async (c) => {
    setMenuOpenId(null);
    if (!confirm(
      `Delete "${c.name}"?\n\nThis permanently removes the campaign and all ${c.totalContacts || 0} of its contacts. ` +
      `Call transcripts and any tenant-wide knowledge stay. This can't be undone.\n\n` +
      `Tip: use "Archive" instead if you just want to hide it.`
    )) return;
    setBusyId(c.id);
    try {
      await campaignsAPI.remove(c.id);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  const create = async () => {
    if (!draft.name.trim()) return;
    try {
      const r = await campaignsAPI.create({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined
      });
      setCreating(false);
      setDraft({ name: '', description: '' });
      navigate(`/campaigns/${r.data.id}`);
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  // Business matches across every campaign. Same matching rules as the in-campaign
  // search: name substring, or phone once the query has 3+ digits.
  const bq = bizQuery.trim().toLowerCase();
  const bqDigits = bq.replace(/\D/g, '');
  const bizMatches = (bq.length >= 2 && bizIndex)
    ? bizIndex.filter((r) => {
        if (`${r.company} ${r.name}`.toLowerCase().includes(bq)) return true;
        return bqDigits.length >= 3 && r.phone.replace(/\D/g, '').includes(bqDigits);
      }).slice(0, 100)
    : [];
  const bizByCampaign = bizMatches.reduce((acc, r) => {
    (acc[r.campaignId] = acc[r.campaignId] || { name: r.campaignName, rows: [] }).rows.push(r);
    return acc;
  }, {});

  // Arrived from a demo row and the business resolves to exactly ONE contact → go
  // straight to its call history. Two clicks would still beat copy-and-paste, but
  // "click the name, see the calls" is the whole request.
  //
  // Guarded so it can only fire on the URL-driven arrival: `autoJump` clears the
  // moment it fires or the query is edited, so typing a name that happens to narrow
  // to one match never teleports you mid-keystroke.
  useEffect(() => {
    if (!autoJump || indexing || !bizIndex) return;
    if (bizMatches.length !== 1) {
      setAutoJump(false);   // zero or many — the results page explains itself
      return;
    }
    const only = bizMatches[0];
    setAutoJump(false);
    navigate(`/campaigns/${only.campaignId}?q=${encodeURIComponent(only.company || only.name)}`);
  }, [autoJump, indexing, bizIndex, bizMatches, navigate]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? [...items].filter((c) =>
        `${c.name || ''} ${c.description || ''}`.toLowerCase().includes(q))
    : [...items];

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Outbound campaigns"
        subtitle="Dial through a list of contacts with a per-campaign pitch"
        actions={
          <>
            <label className="inline-flex items-center gap-2 text-sm text-stone-400 cursor-pointer select-none mr-1">
              <input
                type="checkbox"
                className="accent-alfred-gold"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                className="field-input py-1.5 pl-9 pr-8 text-sm w-56"
                placeholder="Find a campaign…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search campaigns by name or description"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                  aria-label="Clear search"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                className="field-input py-1.5 pl-9 pr-8 text-sm w-56"
                placeholder="Find a business…"
                value={bizQuery}
                onChange={(e) => { setAutoJump(false); setBizQuery(e.target.value); }}
                aria-label="Search all campaigns for a business"
              />
              {bizQuery && (
                <button
                  type="button"
                  onClick={() => setBizQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                  aria-label="Clear business search"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCcw size={14} /> Refresh
            </button>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Plus size={14} /> New campaign
            </button>
          </>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      {creating && (
        <div className="card mb-6 border-alfred-gold/30">
          <h2 className="text-stone-100 font-medium mb-3">New campaign</h2>
          <div className="space-y-3">
            <input
              className="field-input"
              placeholder="Campaign name (e.g. Q2 Houston clinics)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus
            />
            <textarea
              className="field-input min-h-[80px]"
              placeholder="Internal description (optional)"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={create}>Create</button>
              <button type="button" className="btn-ghost" onClick={() => { setCreating(false); setDraft({ name: '', description: '' }); }}>Cancel</button>
            </div>
            <p className="text-xs text-stone-500">
              Once created, you'll add the system-prompt pitch, opening line, calling
              hours, contacts, and dial settings on the campaign detail page.
            </p>
          </div>
        </div>
      )}

      {/* Business results sit ABOVE the campaign table: when you searched for a
          business, the answer is "which campaign", not "here is the campaign list". */}
      {bizQuery.trim().length >= 2 && (
        <div className="card mb-6">
          {indexing ? (
            <p className="text-sm text-stone-400">
              Searching every campaign for “{bizQuery.trim()}”… <span className="text-stone-500">(first search only — it’s cached after this)</span>
            </p>
          ) : indexError ? (
            <p className="text-sm text-red-200">{indexError}</p>
          ) : bizMatches.length === 0 ? (
            <p className="text-sm text-stone-400">
              No business matching “{bizQuery.trim()}” in any campaign
              {bizIndex ? ` (${bizIndex.length} contacts searched)` : ''}.
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-stone-400">
                <span className="text-stone-100">{bizMatches.length}</span> match{bizMatches.length === 1 ? '' : 'es'} in{' '}
                <span className="text-stone-100">{Object.keys(bizByCampaign).length}</span> campaign
                {Object.keys(bizByCampaign).length === 1 ? '' : 's'}
                {bizMatches.length === 100 && <span className="text-stone-500"> · showing the first 100</span>}
              </p>
              <div className="space-y-4">
                {Object.entries(bizByCampaign).map(([cid, grp]) => (
                  <div key={cid}>
                    <div className="mb-1 text-xs uppercase tracking-wider text-stone-500">{grp.name}</div>
                    <ul className="divide-y divide-stone-800">
                      {grp.rows.map((r, i) => (
                        <li key={cid + i} className="flex flex-wrap items-baseline justify-between gap-x-3 py-2 text-sm">
                          <div className="min-w-0">
                            {/* Deep-link: opens the campaign with this business already
                                searched, so you land on the row, not on 881 rows. */}
                            <button
                              type="button"
                              className="text-stone-200 hover:text-alfred-gold underline-offset-2 hover:underline"
                              onClick={() => navigate(`/campaigns/${cid}?q=${encodeURIComponent(r.company || r.name)}`)}
                            >
                              {r.company || r.name || formatPhone(r.phone)}
                            </button>
                            <span className="text-stone-500"> · {formatPhone(r.phone)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-stone-500">
                            <span>{r.status || '—'}</span>
                            {r.outcome && <span className="text-stone-400">{r.outcome.replace(/_/g, ' ')}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Contacts</th>
              <th className="px-4 py-3 font-medium">Progress</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">
                No campaigns yet. Click <em>New campaign</em> to start one.
              </td></tr>
            ) : visible.length === 0 ? (
              /* A search miss is NOT an empty account — saying "no campaigns yet"
                 here would tell someone with 20 campaigns that they have none. */
              <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">
                No campaign matches “{query}”.{' '}
                <button type="button" className="underline hover:text-stone-300" onClick={() => setQuery('')}>
                  Clear the search
                </button>
                {!showArchived && ' — archived campaigns are hidden.'}
              </td></tr>
            ) : visible.sort(byStatusPriority).map((c) => {
              const pct = c.totalContacts > 0
                ? Math.round((c.contactsCompleted / c.totalContacts) * 100)
                : 0;
              return (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/campaigns/${c.id}`)}
                  className="border-b border-stone-900 hover:bg-stone-900/50 cursor-pointer transition"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Megaphone size={14} className="text-alfred-gold" />
                      <span className="text-stone-100 font-medium">{c.name}</span>
                      {c.archived && (
                        <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">archived</span>
                      )}
                    </div>
                    {/* Guard on the STRIPPED text: a description that is nothing but
                        the auto-pause marker would otherwise render an empty row. */}
                    {stripPauseMarker(c.description) && (
                      <div className="text-xs text-stone-500 mt-0.5 truncate max-w-md">{stripPauseMarker(c.description)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`badge ${STATUS_BADGE[c.status] || ''}`}>{c.status}</span>
                      <AutoPauseChip campaign={c} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-300 tabular-nums">{c.totalContacts}</td>
                  <td className="px-4 py-3 text-stone-300 tabular-nums">{pct}% ({c.contactsCompleted}/{c.totalContacts})</td>
                  <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{formatDateTime(c.createdAt)}</td>
                  <td className="px-4 py-3 text-stone-500">
                    <div className="flex items-center justify-end gap-1">
                      {/* Per-campaign action menu — stopPropagation so clicks
                          here don't navigate into the campaign. */}
                      <div className="relative" ref={menuOpenId === c.id ? menuRef : null}>
                        <button
                          type="button"
                          className="p-1.5 rounded-md hover:bg-stone-800 text-stone-400 hover:text-stone-200 disabled:opacity-50"
                          disabled={busyId === c.id}
                          onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === c.id ? null : c.id); }}
                          aria-label="Campaign actions"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                        {menuOpenId === c.id && (
                          <div
                            className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-stone-700 bg-stone-900 shadow-xl py-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-200 hover:bg-stone-800"
                              onClick={() => toggleArchived(c)}
                            >
                              {c.archived
                                ? (<><ArchiveRestore size={14} /> Unarchive</>)
                                : (<><Archive size={14} /> Archive</>)}
                            </button>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10"
                              onClick={() => remove(c)}
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                      <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
